import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';

import { Alert, AlertAction } from '@simplywallst/ui-core';

import { useSnackbar } from '@/features/snackbar';

import type { Portfolio } from '@/hooks/portfolio/usePortfolio';
import {
  PORTFOLIO_QUERY_KEY,
  PORTFOLIOS_QUERY_KEY,
} from '@/hooks/portfolio/usePortfolio';

import type { PortfolioLinkingSDKError } from '../LinkingProviders/error/PortfolioLinkingSDKError';
import { isPortfolioLinkingSDKError } from '../LinkingProviders/error/PortfolioLinkingSDKError';
import { capturePortfolioLinkingError } from '../LinkingProviders/utils/capturePortfolioLinkingError';

import { LinkStatusWrapper } from './styled';

const PlaidPortfolioReconnector = loadable(
  () => import('../LinkingProviders/Plaid/PlaidPortfolioReconnector'),
  { resolveComponent: (c) => c.PlaidPortfolioReconnector }
);
const PassivPortfolioReconnector = loadable(
  () => import('../LinkingProviders/Passiv'),
  {
    resolveComponent: (component) => component.PassivPortfolioReconnector,
  }
);
const SharesightPortfolioReconnector = loadable(
  () => import('../LinkingProviders/Sharesight'),
  {
    resolveComponent: (component) => component.SharesightPortfolioReconnector,
  }
);

interface Props {
  portfolio: Portfolio;
}

const DisconnectedState = ({ portfolio }: Props) => {
  const { addSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { Track, trackEvent } = useTracking({
    modifier: 'reconnectWithBrokerage',
  });

  const handleClick = useCallback(
    (portfolioId: Portfolio['id']) => {
      setShowModal(true);
      trackEvent({ action: 'click', portfolioId });
    },
    [trackEvent]
  );

  const handleClose = useCallback(() => {
    setShowModal(false);
    trackEvent({ action: 'closed' });
  }, [trackEvent]);

  const handleSuccess = useCallback(() => {
    setShowModal(false);
    trackEvent({ action: 'complete' });
    queryClient.invalidateQueries({
      queryKey: PORTFOLIOS_QUERY_KEY,
    });
    queryClient.invalidateQueries({
      queryKey: [PORTFOLIO_QUERY_KEY, portfolio.id],
    });
    addSnackbar({
      type: 'positive',
      lifeTime: 2000,
      message: 'Portfolio successfully reconnected with your brokerage',
    });
  }, [queryClient, trackEvent, addSnackbar, portfolio.id]);

  /**
   * Note: This event handler aims to unify the error handling for all linking providers,
   * covering both SDK errors and SWS errors during the linking flow.
   */
  const handleError = useCallback(
    (error?: PortfolioLinkingSDKError) => {
      if (isPortfolioLinkingSDKError(error)) {
        trackEvent({ action: 'providerError' });
        setShowModal(false);
        addSnackbar({
          type: 'negative',
          lifeTime: 3000,
          message: error.message,
        });
        capturePortfolioLinkingError({
          provider: portfolio.provider.name,
          brokerage: portfolio.link.brokerageName,
          flow: 'reconnection',
          error,
        });
        return;
      }

      /**
       * NOTE: This is temporary until we introduce a new error type to cover the errors from SWS BE during linking flow.
       * Once its done, the following code will be executed after checking the PortfolioLinkingSWSError type.
       * Also we can show a better error message once we start using PortfolioLinkingSWSError
       */
      trackEvent({ action: 'error' });
      setShowModal(false);
      addSnackbar({
        type: 'negative',
        lifeTime: 3000,
        message: 'Failed to reconnect your portfolio with your brokerage',
      });
    },
    [
      addSnackbar,
      portfolio.link.brokerageName,
      portfolio.provider.name,
      trackEvent,
    ]
  );

  return (
    <Track>
      <LinkStatusWrapper>
        <Alert
          data-cy-id="alert-disconnected-message"
          styleType="warning"
          layout="stack"
          actions={
            <AlertAction
              shouldDismissAlert={false}
              onClick={() => handleClick(portfolio.id)}
            >
              Reconnect
            </AlertAction>
          }
        >
          Re-authentication required. Please update connection to retrieve
          latest data from your brokerage.
        </Alert>
      </LinkStatusWrapper>

      {showModal && portfolio.provider.name === 'passiv' ? (
        <PassivPortfolioReconnector
          portfolioId={portfolio.id}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={handleClose}
        />
      ) : null}

      {showModal && portfolio.provider.name === 'sharesight' ? (
        <SharesightPortfolioReconnector
          portfolioId={portfolio.id}
          onError={handleError}
        />
      ) : null}

      {showModal && portfolio.provider.name === 'plaid' ? (
        <PlaidPortfolioReconnector
          portfolioId={portfolio.id}
          onSuccess={handleSuccess}
          onError={handleError}
          onClose={handleClose}
        />
      ) : null}
    </Track>
  );
};

export default DisconnectedState;
